import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import moment from 'moment-timezone';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareDecisions = (d1, d2) => d1.id === d2.id;

const decisionSlice = createSlice({
    name: 'decision',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload;
        },
        add: (state, action) => {
            for (const d of ensureArray(action.payload)) {
                let idx = state.data.findIndex(d2 => compareDecisions(d, d2));
                if (idx >= 0) {
                    state.data.splice(idx, 1, d);
                } else {
                    state.data.push(d);
                }
            }
        },
        remove: (state, action) => {
            for (const decision of ensureArray(action.payload)) {
                let idx = state.data.findIndex(d => compareDecisions(d, decision));
                if (idx >= 0) {
                    state.data.splice(idx, 1);
                }
            }
        },
    },
    extraReducers: builder => {
        builder.addCase(logout, () => decisionSlice.getInitialState());
    }
});

// #region network operations
const fetchDecision = id => network => network.get('/api/v1/decision/' + encodeURIComponent(id), decisionSlice.actions.add);
const fetchJobDecisions = jobid => network => network.get('/api/v1/jobDecisions/' + encodeURIComponent(jobid), decisionSlice.actions.add);
const fetchTypeDecisions = (site, level, type) => network => network.post('/api/v1/typeDecisions', { site, level, type }, decisionSlice.actions.add);
const fetchTimeDecisions = (start, end) => network => network.get('/api/v1/decisions/' + encodeURIComponent(start) + '/' + encodeURIComponent(end), decisionSlice.actions.add);

export const createDecision = (network, decision) => network.post('/api/v1/decision', decision, decisionSlice.actions.add);
// #endregion

// #region selectors
const selectDecisions = state => state.decision.data;

const selectDecisionById = createSelector([selectDecisions, (decisions, id) => id], (decisions, id) => decisions.find(d => d.id === id));
const selectDecision = id => state => selectDecisionById(state, id);

const selectDecisionsByJob = createSelector([selectDecisions, (decisions, jobid) => jobid], (decisions, jobid) => decisions.filter(d => d.jobs.some(j => j.toString() === jobid.toString())));
const selectJobDecisions = jobid => state => selectDecisionsByJob(state, jobid);

const selectDecisionsBySLT = createSelector([selectDecisions, (decisions, site) => site, (decisions, site, level) => level, (decisions, site, level, type) => type], (decisions, site, level, type) => decisions.filter(d => (!site || d.site === site) && (!level || d.level === level) && (!type || d.type === type)));
const selectTypeDecisions = (site, level, type) => state => selectDecisionsBySLT(state, site, level, type);

const selectDecisionsByTime = createSelector([selectDecisions, (decisions, start) => start, (decisions, start, end) => end], (decisions, start, end) => {
    const ms = moment(start), me = moment(end);
    return decisions.filter(d => moment(d.start).isBetween(ms, me, '[)'));
});
const selectTimeDecisions = (start, end) => state => selectDecisionsByTime(state, start, end);
// #endregion

// #region listeners
const listen = {
    decisionCreated: (decision, { dispatch }) => dispatch(decisionSlice.actions.add(decision))
}
// #endregion
const options = {
    cleanup: { root: 'decisions', all: selectDecisions, removeAll: decisionSlice.actions.remove, remove: decisionSlice.actions.remove }
}

export const [useDecision] = dataHookFactory(selectDecision, initMapFactory(fetchDecision, listen), options);
export const [useTypeDecisions] = dataHookFactory(selectTypeDecisions, initMapFactory(fetchTypeDecisions, listen), options);
export const [useJobDecisions] = dataHookFactory(selectJobDecisions, initMapFactory(fetchJobDecisions, listen), options);
export const [useTimeDecisions] = dataHookFactory(selectTimeDecisions, initMapFactory(fetchTimeDecisions, listen), options);

const reducerDetails = { name: decisionSlice.name, reducer: decisionSlice.reducer };
export default reducerDetails;